import { useLazyQuery } from '@apollo/client/react/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { APP_CONFIG } from '@/config';
import { GET_DEVICE_CONFIG } from '@/queries/devices';
import { useSelection } from '@/libs/onboarding';
import { storageSvc } from '@/services/storageServiceClient';
import {
  OrbDeviceConfig,
  DeviceMode,
  DeviceEventActions,
  DeviceEvent,
} from '@oolio-group/domain';
import {
  DARK_THEME,
  LIGHT_THEME,
  useTheme,
} from '@oolio-group/design-ui-react';
import { useDeviceEvents } from './useDeviceEvents';

export interface SalesChannel {
  id: string;
  name: string;
}

export interface UseDeviceConfigReturn {
  orbConfig: OrbDeviceConfig;
  salesChannels: SalesChannel[];
  loading: boolean;
  getSalesChannelName: (id: string) => string | undefined;
}

/**
 * Hook to manage device configuration and sales channels
 *
 * @returns Device configuration, sales channels and helper functions
 */
export const useDeviceConfig = (): UseDeviceConfigReturn => {
  const { device } = useSelection(storageSvc);
  const { setTheme } = useTheme();
  // Fetch selected device configuration and sales channels in single query
  const [fetchDeviceConfig, { data, loading }] = useLazyQuery(
    GET_DEVICE_CONFIG,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onError: error => {
        console.error('GET_DEVICE_CONFIG failed', error);
      },
    },
  );

  const orbConfig = useMemo(() => {
    const device = data?.device;
    const deviceConfig = device?.config;

    return {
      type: DeviceMode.ORB,
      orderTypeIds: deviceConfig?.orderTypeIds || [],
      printerProfileIds: deviceConfig?.printerProfileIds || [],
      autoClearTimeout:
        deviceConfig?.autoClearTimeout || APP_CONFIG.AUTO_CLEAR_TIMEOUT_SECONDS,
      theme: deviceConfig?.theme || { dark: false },
    };
  }, [data]);

  useEffect(() => {
    // Set theme based on device configuration
    if (orbConfig.theme?.dark) {
      setTheme(DARK_THEME);
    } else {
      setTheme(LIGHT_THEME);
    }
  }, [orbConfig.theme?.dark, setTheme]);

  const handleConfigUpdate = useCallback(
    async (event: DeviceEvent) => {
      if (event.deviceId !== device?.id) {
        return;
      }
      fetchDeviceConfig({ variables: { id: device.id } });
    },
    [device?.id, fetchDeviceConfig],
  );

  useDeviceEvents([
    {
      action: DeviceEventActions.CONFIG_UPDATE_ACTION,
      handler: handleConfigUpdate,
    },
  ]);

  const salesChannels = useMemo(() => {
    return data?.salesChannels || [];
  }, [data]);

  const getSalesChannelName = useCallback(
    (id: string): string | undefined => {
      const channel = salesChannels.find(
        (channel: SalesChannel) => channel.id === id,
      );
      return channel?.name;
    },
    [salesChannels],
  );

  useEffect(() => {
    if (!device?.id) return;
    fetchDeviceConfig({ variables: { id: device.id } });
  }, [device?.id, fetchDeviceConfig]);

  return {
    orbConfig,
    salesChannels,
    loading,
    getSalesChannelName,
  };
};
